import { BreakpointObserver } from '@angular/cdk/layout';
import { StepperOrientation } from '@angular/cdk/stepper';
import { Location } from '@angular/common';
import { AfterViewInit, Component, computed, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { Usager } from '@app/models/consumer';
import { DemarcheFormReponse, DemarcheItem, DemarcheStep, entity, ReponseStepQuestion } from '@app/models/demarche';
import { GedEntity } from '@app/models/ged';
import { FamilyService, PermissionService, PlatformService } from '@app/services';
import { DemarcheService } from '@app/services/demarche.service';
import { HeaderService } from '@app/services/header.service';
import { StorageService } from '@app/services/storage.service';
import { countAndCheckFollowingItems } from '@app/utils/array';
import { clone } from '@app/utils/object';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Component({
  selector: 'app-demarche',
  templateUrl: './demarche.component.html',
  styleUrls: ['./demarche.component.scss'],
  standalone: false
})
export class DemarcheComponent implements OnInit, AfterViewInit {

  demarche: DemarcheItem;
  stepperOrientation: Observable<StepperOrientation>;
  currentUsager: Pick<Usager, 'id' | 'type'>;
  idDemarche: number;
  idFamille: number;
  addDocumentPermission: boolean;
  showMessageDemarcheCompleted: boolean;
  fromDemarchesList: boolean;
  url: string;
  entities = signal<entity[]>([]);

  @ViewChild(MatStepper) stepper: MatStepper;
  @ViewChild('resumeDialog') resumeDialogTemplate: TemplateRef<any>;

  constructor(
    private headerService: HeaderService,
    private demarcheService: DemarcheService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private location: Location,
    public breakpointObserver: BreakpointObserver,
    private permissionService: PermissionService,
    public platformService: PlatformService,
    public storageService: StorageService,
    private familyService: FamilyService,
    private dialog: MatDialog,
  ) {
    // Configuration de l'orientation du stepper basée sur la largeur de l'écran
    this.stepperOrientation = breakpointObserver
      .observe('(min-width: 800px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));
  }

  ngOnInit(): void {
    // Récupération de l'identifiant de la démarche à partir des paramètres de la route
    this.idDemarche = this.activatedRoute.snapshot.params.idDemarche;
    this.url = this.router.url;
    this.idFamille = this.familyService.currentFamily.id;

    // Vérification de la permission d'ajouter des documents
    this.addDocumentPermission = this.permissionService.hasPermission('document_add');

    if (!this.url?.includes('demarches')) {
      this.loadDemarche();
      this.initEntities();
    }
  }

  ngAfterViewInit(): void {
    if (this.url?.includes('demarches')) {

      const saved = this.demarcheService.get(this.idDemarche, this.idFamille);

      if (saved) {
        const dialogRef = this.dialog.open(this.resumeDialogTemplate, {
          data: {
            label: saved.demarche?.label || `Démarche #${this.idDemarche}`
          },
          disableClose: true
        });

        dialogRef.afterClosed().subscribe(resume => {
          if (resume) {
            this.demarche = saved.demarche;
            this.currentUsager = saved.usager;
            const stepIndex = saved.currentStep ?? 0;
            setTimeout(() => {
              if (this.stepper && saved.currentStep != null) {
                this.headerService.setCurrentPageTitle(this.demarche.label);
                this.goToStep(stepIndex);
              }
            });
            this.entities.set(saved.entities);
          } else {
            this.demarcheService.clear(this.idDemarche);
            this.loadDemarche();
            this.initEntities();
          }
        });
      } else {
        setTimeout(() => {
          this.loadDemarche();
          this.initEntities();
        });
      }

    }
  }

  loadDemarche() {
    this.demarcheService.getDemarcheById(this.idDemarche).pipe(take(1)).subscribe(demarche => {
      if (!demarche) {
        // Si la démarche n'existe pas, rediriger vers la page précedente
        history.back();
      } else {
        // Cloner les données pour éviter des modifications non intentionnelles
        this.demarche = clone(demarche);

        // Si l'utilisateur peut ajouter des documents, enrichir les étapes
        if (this.addDocumentPermission) {
          const newSteps: DemarcheStep[] = [];
          this.demarche.steps.forEach(step => {
            newSteps.push(step);
            if (step.docPieceStep && step.activePieceAFournir) {
              newSteps.push(step.docPieceStep);
            }
          });
          this.demarche.steps = newSteps;
        }

        // Mise à jour du titre de la page avec le label de la démarche
        this.headerService.setCurrentPageTitle(this.demarche.label);
      }
    });
  }

  initEntities() {
    this.entities.set([{
      id: this.idFamille,
      type: "famille",
      typeStructure: "Domino",
      indexStep: null
    }]);
  }

  // Gestion de la sélection d'un usager
  saveSelectedUsager(usager: Usager, step: DemarcheStep, index: number): void {
    this.setCurrentUsager(usager);
    step.usagerSelected = this.currentUsager;

    const gedEntity = this.createGedEntityFromUsager(this.currentUsager);

    const nextStep = this.demarche.steps[index + 1];
    if (nextStep.type === 'piece') {
      nextStep.gedEntity = gedEntity;
    }

    this.updateEntities(index, gedEntity);
    this.afterValidateStep(step, index);
  }

  setCurrentUsager(usager: Usager): void {
    this.currentUsager = usager;
  }

  createGedEntityFromUsager(usager: Pick<Usager, "id" | "type">): GedEntity {
    return {
      id: usager.id,
      type: usager.type,
      typeStructure: 'Domino'
    };
  }

  // Ajout d'une étape pour ajouter un formulaire spécifique (Enfant ou Conjoint)
  addStepToCreate(formName: 'Enfant' | 'Conjoint', step: DemarcheStep, index: number) {

    this.removeEntity(index)

    const formStep = step.stepsForCreationMode.find(formStep => formStep.formName === formName);

    formStep.addedFromSelect = true;
    formStep.dependentReponseId = step?.dependentReponseId;
    formStep.dependentQuestionId = step?.dependentQuestionId;
    formStep.dependenciesQuestion = step?.dependenciesQuestion;

    this.demarche.steps.splice(index + 1, 0, formStep);

    for (let i = index + 1; i < this.demarche.steps.length; i++) {
      this.demarche.steps[i].completed = false;
      this.demarche.steps[i].editable = false;
    }

    this.afterValidateStep(step, index);
  }

  // Validation d'une étape Formulaire
  onValidateStepForm(event: DemarcheFormReponse, step: DemarcheStep, index: number): void {
    if (!event) return;

    this.setCurrentUsagerFromEvent(event);
    const gedEntity = this.createGedEntityFromEvent(event);

    step.idEntitySaved = event.id ?? null;
    step.usagerSelected = this.currentUsager;

    const isFamilialForm = ['Conjoint', 'Enfant', 'Famille', "Utilisateur"].includes(step.formName);

    if (isFamilialForm) {
      this.updateEntities(index, gedEntity);

      if (step.addedFromSelect) {
        const previousStep = this.demarche.steps[index - 1];
        previousStep.usagerSelected = { id: event.id, type: event.type };
      }
    } else {
      this.updateEntities(index, gedEntity, this.currentUsager);
      step.usagerSelected = { id: this.currentUsager?.id, type: this.currentUsager?.type };
    }

    const nextStep = this.demarche?.steps[index + 1];

    if (nextStep?.type === 'piece') {
      nextStep.gedEntity = gedEntity;
    }

    this.afterValidateStep(step, index);
  }

  setCurrentUsagerFromEvent(event: DemarcheFormReponse): void {
    this.currentUsager = event.usager ?? null;
  }

  createGedEntityFromEvent(event: DemarcheFormReponse): GedEntity {
    return {
      id: event.id,
      type: event.type,
      typeStructure: event.typeStructure
    };
  }

  // Gestion de la validation d'une question
  onValidateQuestion(idReponse: number, step: DemarcheStep, index: number): void {

    const selectedReponse = step.reponses.find(r => r.id === idReponse);

    this.ensureStepHasId(step);

    const previousResponseId = step.idReponse;
    const hasChangedResponse = previousResponseId && previousResponseId !== idReponse;

    if (hasChangedResponse) {
      this.cleanStepsIfNeeded(step, previousResponseId, index);

      if (idReponse && selectedReponse?.steps?.length) {
        this.handleResponseUpdate(selectedReponse);
        this.insertSteps(selectedReponse, step, index);
      }

      for (let i = index + 1; i < this.demarche.steps.length; i++) {
        this.demarche.steps[i].completed = false;
        this.demarche.steps[i].editable = false;
      }

    } else if (!previousResponseId) {
      // Premier choix
      if (idReponse && selectedReponse?.steps?.length) {
        this.handleResponseUpdate(selectedReponse);
        this.insertSteps(selectedReponse, step, index);
      }
    }

    step.idReponse = idReponse;
    setTimeout(() => this.afterValidateStep(step, index));
  }

  ensureStepHasId(step: DemarcheStep): void {
    if (!step.idQuestion) {
      step.idQuestion = `id_${Math.random().toString(36).substring(2, 10)}_${Date.now()}`;
    }
  }

  cleanStepsIfNeeded(step: DemarcheStep, previousResponseId: number, index: number): void {
    const isLoop = step.reponses.find(r => r.id === previousResponseId)?.isLoop;

    const { count, hasFlag } = countAndCheckFollowingItems(
      this.demarche.steps,
      index,
      'dependenciesQuestion',
      step.idQuestion ?? '',
      'idEntitySaved'
    );

    if (!hasFlag && count > 0) {
      const deleteCount = isLoop ? count + 1 : count;
      this.demarche.steps.splice(index + 1, deleteCount);
    }
  }

  // Mise à jour de la réponse pour ajouter des étapes spécifiques
  handleResponseUpdate(reponse: ReponseStepQuestion) {
    if (this.addDocumentPermission && !reponse.alreadyUpdated) {
      reponse.steps = reponse.steps.flatMap(step =>
        step.docPieceStep && step.activePieceAFournir ? [step, step.docPieceStep] : [step]
      );
      reponse.alreadyUpdated = true;
    }
  }

  // Insertion des étapes liées à une réponse après la réponse donnée
  insertSteps(reponse: ReponseStepQuestion, currentStep: DemarcheStep, index: number): void {
    const clonedResponse = clone(reponse);
    const clonedStep = clone(currentStep);

    this.prepareClonedSteps(clonedResponse, clonedStep.idQuestion ?? '', reponse.id, clonedStep.dependenciesQuestion ?? []);

    const isSingleStep = this.demarche.steps.length === 1;
    const shouldLoop = clonedResponse.isLoop;

    if (isSingleStep) {
      this.demarche.steps.push(...clonedResponse.steps);

      if (shouldLoop) {
        this.prepareLoopStep(clonedStep);
        this.demarche.steps.push(clonedStep);
      }

    } else {
      this.insertStepsAt(index + 1, clonedResponse.steps);

      if (shouldLoop) {
        const loopInsertIndex = index + 1 + clonedResponse.steps.length;
        this.prepareLoopStep(clonedStep);
        this.demarche.steps.splice(loopInsertIndex, 0, clonedStep);
      }
    }
  }

  prepareClonedSteps(clonedResponse: ReponseStepQuestion, questionId: string, responseId: number, dependenciesQuestion: string[]): void {
    clonedResponse.steps.forEach(step => {
      step.dependentReponseId = responseId;
      step.dependentQuestionId = questionId;
      step.dependenciesQuestion = [...dependenciesQuestion, questionId];
      step.usagerSelected = null;
      step.gedEntity = null;
      step.idEntitySaved = null;
    });
  }

  prepareLoopStep(step: DemarcheStep): void {
    delete step.idQuestion;
    delete step.idReponse;
    delete step.dependentQuestionId;
    delete step.dependentReponseId;
    step.completed = false;
    step.editable = false;
    step.fromLoop = true;
  }

  insertStepsAt(position: number, steps: DemarcheStep[]): void {
    this.demarche.steps.splice(position, 0, ...steps);
  }

  // Gestion de la validation des pièces à fournir (étape piece à fournir)
  onValidatePieceAFournir(event, step: DemarcheStep, index: number) {
    if (event) {
      this.afterValidateStep(step, index);
    }
  }

  // Actions communes après validation d'une étape
  afterValidateStep(step: DemarcheStep, index: number): void {

    step.completed = true;

    const isFinalStep = this.demarche?.steps.length === 1 || this.isLastStep();

    if (isFinalStep) {
      this.handleEndOfDemarche();
    } else {
      setTimeout(() => {
        this.stepper.next();
        this.demarche.steps[this.stepper.selectedIndex].editable = true;
        if (this.url?.includes('demarches')) {
          this.demarcheService.save(this.demarche.id, {
            idFamille: this.idFamille,
            currentStep: this.stepper.selectedIndex,
            demarche: this.demarche,
            entities: this.entities() || [],
            usager: this.currentUsager
          });
        }
      });
    }
  }

  handleEndOfDemarche(): void {
    if (this.demarche.messageCompletedDemarche) {
      this.showMessageDemarcheCompleted = true;

      if (this.url?.includes('demarches')) {
        this.fromDemarchesList = true;
      }
    } else {
      if (this.url?.includes('demarches')) {
        this.router.navigate(['account']);
      } else {
        this.location.back();
      }
    }
    this.demarcheService.clear(this.demarche.id);
  }

  onStepChange(index: number): void {
    if (this.url?.includes('demarches')) {
      this.demarcheService.save(this.demarche.id, {
        idFamille: this.idFamille,
        currentStep: index,
        demarche: this.demarche,
        entities: this.entities() || [],
        usager: this.currentUsager
      });
    }
  }

  goToStep(index: number): void {

    if (this.stepper && index >= 0 && index < this.stepper._steps.length) {

      this.stepper.selectedIndex = index;

      const currentStep = this.demarche.steps[index];
      const nextStep = this.demarche.steps[index + 1];
      const previousStep = this.demarche.steps[index - 1];

      if (currentStep?.type === 'select' && nextStep?.type === 'form' && nextStep?.addedFromSelect && !nextStep?.idEntitySaved) {
        this.removeEntity(index)
        this.demarche.steps.splice(index + 1, 1);
      }

      if (previousStep?.type === 'form' && previousStep?.addedFromSelect && !previousStep?.idEntitySaved) {
        this.demarche.steps.splice(index - 1, 1);
      }
    }
  }

  isLastStep(): boolean {
    if (this.stepper) {
      return this.stepper.selectedIndex === this.stepper.steps.length - 1;
    } else {
      return false;
    }
  }

  onPreviousStep(index: number, step: DemarcheStep) {
    if (step.addedFromSelect && !step.idEntitySaved) {
      this.removeEntity(index)
      this.demarche.steps.splice(index, 1);
    }
    this.stepper.previous();
  }

  updateEntities(index: number, gedEntite: GedEntity, usager: Pick<Usager, "id" | "type"> = null): void {
    const basicEntity: entity = {
      id: gedEntite.id,
      type: gedEntite.type,
      typeStructure: gedEntite.typeStructure,
      indexStep: index
    };

    const usagerEntity: entity | null = usager ? {
      id: usager.id,
      type: usager.type,
      typeStructure: "Domino",
      indexStep: index
    } : null;

    const newEntities = [
      basicEntity,
      ...(usagerEntity ? [usagerEntity] : [])
    ];

    this.entities.update(entities =>
      [
        ...entities.filter(item => item.indexStep !== index),
        ...newEntities
      ]
    );
  }

  removeEntity(index: number): void {
    this.entities.update(entities =>
      entities.filter(item => item.indexStep !== index)
    );
  }

  getEntitiesBeforeStep = (index: number) => computed(() =>
    this.entities().filter(entite => entite.indexStep < index || entite.type.toLowerCase() === "famille")
  );

  // setAllowEditing(step: DemarcheStep) {
  //   if (step.type === "form") {
  //     switch (step.formName) {
  //       case 'Enfant': step.allowEditing = this.permissionService.hasPermission('child_edit'); break;
  //       case 'Pré-inscription Mikado': step.allowEditing = this.permissionService.hasPermission('preinscription_edit'); break;
  //       case 'Inscription Diabolo': step.allowEditing = this.permissionService.hasPermission('diabolo_inscriptions_edit'); break;
  //       case 'Inscription Cogito': step.allowEditing = this.permissionService.hasPermission('cogito_inscriptions_edit'); break;
  //       case 'Pré-inscription Lasido': step.allowEditing = this.permissionService.hasPermission('lasido_edit'); break;
  //       case 'Pré-inscription Cogito': step.allowEditing = this.permissionService.hasPermission('cogito_edit'); break;
  //       case 'Famille':
  //       case 'Conjoint': step.allowEditing = this.permissionService.hasPermission('foyer_edit'); break;
  //     }
  //   } else step.allowEditing = true;
  // }
}
